import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Crown, Gift } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { GiftCardDialog } from "@/components/admin/GiftCard";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtDate, inputCls } from "@/components/hub/Common";

export const SuspendBtn = ({ suspended, onClick, testid }) => {
  const { t } = useI18n();
  return (
    <button data-testid={testid} onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${suspended ? "border-[#22e3a1]/50 text-[#22e3a1] hover:bg-[#22e3a1]/10" : "border-rose-500/40 text-rose-400 hover:bg-rose-500/10"}`}>
      {suspended ? t("adm.reactivate") : t("adm.suspend")}
    </button>
  );
};

const GiftBtn = ({ u, onDone }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [clips, setClips] = useState(5);
  const [note, setNote] = useState("");
  const [card, setCard] = useState(null);
  const send = async () => {
    try { const { data } = await api.post(`/admin/users/${u.id}/gift`, { clips, note }); toast.success(t("adm.gift_done").replace("{n}", clips).replace("{e}", u.email)); setOpen(false); setNote(""); setCard(data.card); onDone?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <>
    <GiftCardDialog card={card} onClose={() => setCard(null)} />
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`admin-user-gift-${u.id}`} className="rounded-full px-3 py-1.5 text-xs border border-[#22e3a1]/40 text-[#22e3a1] hover:bg-[#22e3a1]/10 inline-flex items-center gap-1"><Gift className="w-3.5 h-3.5" />{t("adm.gift_btn")}</button>
      </PopoverTrigger>
      <PopoverContent className="bg-[#0b0e1a] border-white/10 w-72 space-y-3" data-testid={`admin-gift-popover-${u.id}`}>
        <label className="block text-[11px] text-slate-400">{t("adm.gift_clips")}
          <input type="number" min={1} max={1000} value={clips} onChange={(e) => setClips(Math.max(1, Math.min(1000, Number(e.target.value) || 1)))} className={`${inputCls} mt-1`} data-testid="admin-gift-clips" />
        </label>
        <label className="block text-[11px] text-slate-400">{t("adm.gift_note")}
          <input maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} className={`${inputCls} mt-1`} data-testid="admin-gift-note" />
        </label>
        <button onClick={send} className="cx-btn w-full rounded-full py-2 text-sm font-semibold" data-testid="admin-gift-send">{t("adm.gift_send")}</button>
      </PopoverContent>
    </Popover>
    </>
  );
};

export const UsersTab = () => {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/admin/users", { params: { q } }).then((r) => setList(r.data)), [q]);
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [load]);
  const toggle = async (u) => {
    try { await api.post(`/admin/users/${u.id}/suspend`, { suspended: !u.suspended }); toast.success(t("adm.saved")); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
        <input data-testid="admin-users-search" className={`${inputCls} pl-11`} placeholder={t("adm.search")} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="cx-card rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]" data-testid="admin-users-table">
          <thead><tr className="text-left text-[11px] font-mono uppercase tracking-wider text-slate-500 border-b border-white/5">
            {["Email", "Pseudo", "Workspace", "Role", "Clips", t("adm.credits_col"), t("adm.consent"), "Date", ""].map((h, i) => <th key={i} className="px-4 py-3 font-normal">{h}</th>)}
          </tr></thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`admin-user-row-${u.id}`}>
                <td className="px-4 py-3 text-white"><span className="flex items-center gap-2">{u.is_superadmin && <Crown className="w-3.5 h-3.5 text-[#ffc94d]" />}{u.email}</span></td>
                <td className="px-4 py-3 text-slate-300">{u.name}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-400">{u.tenant_id}</td>
                <td className="px-4 py-3 text-slate-400">{u.role}</td>
                <td className="px-4 py-3 text-slate-300">{u.clips}</td>
                <td className="px-4 py-3 font-mono text-[#ffc94d]" data-testid={`admin-user-credits-${u.id}`}>{u.credits}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{u.terms_version || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap space-x-2"><GiftBtn u={u} onDone={load} />{u.is_superadmin ? <span className="text-xs text-[#ffc94d]">{t("adm.owner")}</span> : <SuspendBtn testid={`admin-user-suspend-${u.id}`} suspended={u.suspended} onClick={() => toggle(u)} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
